import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CheckCircle2, Loader2, Paperclip, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useCurrentUserProfile } from '@/hooks/useCurrentUserProfile';
import { useClinicUsersList } from '@/hooks/useClinicUsers';
import { useClinic } from '@/hooks/useClinicSettings';
import { useSorEntityName } from '@/hooks/useSorEntityName';
import { officialCprDigits } from '@/lib/cpr';
import { ApiCallError } from '@/lib/api';
import { labShareCheck } from '@/lib/lab/labShareCheck';
import {
  AUTHOR_ROLE_CODES,
  CARE_COMMUNICATION_CATEGORIES,
  useBundleJson,
  useCareCommunicationThread,
  useClinicCommunicateConnection,
  useClinicSenderIdentities,
  useSendCareCommunication,
  useThreadLetterBundles,
  type CareCommunicationCategory,
  type PayloadSegmentInput,
  type SendMessageType,
} from '@/hooks/useCareCommunications';
import { composeOutgoingPayloads } from '@/lib/medcom/newPayloads';
import { Button } from '@/components/ui/Button';
import { PAGE_TITLE } from '@/components/ui/pageChrome';
import { Input } from '@/components/ui/Input';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { attachmentCreationTime } from './attachmentCreation';
import { ForwardPriorMessage } from './PriorMessagePreview';
import { ThreadAttachmentPicker } from './ThreadAttachmentPicker';
import { useForwardChoice } from './useForwardChoice';
import { useForwardReceiverName } from './useForwardReceiverName';
import { usePatientGivenFallback } from './usePatientGivenFallback';
import { outgoingTopic, topicRequired } from '@/lib/medcom/composeTopic';
import {
  composeReceiver,
  listedPatientIdentifiers,
  newLetterPatientComposeState,
  patientIdentifiersFromField,
  replyLetterFields,
  replyPatientFields,
  replyPatientSource,
  type PatientIdKind,
  type ReplyPatientIdentifiers,
  type ReplyCareTeam,
  type ReplyRecipient,
  specificRecipientBody,
} from '@/lib/medcom/replyPrefill';
import { patientIdentityBadgeLabel } from '@/lib/medcom/letterDisplay';
import {
  AUTHOR_FREE_TEXT,
  authorFromClinicUser,
  clinicAuthorizedSenders,
  composeClinicSender,
  preferredReplySender,
  replySenderChoices,
} from '@/lib/medcom/composeClinicSender';
import { episodeOfCareLabel, replyEncounterPayload } from '@/lib/medcom/parseCareCommunicationBundle';
import { newestMessageId } from '@/lib/medcom/priorSegments';
import { replyParentMessageId } from '@/lib/medcom/replyParent';
import {
  alreadyForwardedIdentifiers,
  attachmentTitleFromFileName,
  carriedAttachmentPayloads,
  mayCarryThreadAttachments,
  newAttachmentIdentifier,
  pickerThreadAttachments,
  threadAttachmentsFromLetters,
} from '@/lib/medcom/threadAttachments';

/** MedCom ValueSet medcom-core-attachmentMimeTypes — only these 6 are allowed. */
const ALLOWED_ATTACHMENT_MIME_TYPES = [
  'application/pdf',
  'image/gif',
  'image/jpeg',
  'image/png',
  'image/tiff',
  'image/bmp',
] as const;

function attachmentTitle(fileName: string): string {
  return attachmentTitleFromFileName(fileName);
}

function mimeLabel(mime: AllowedAttachmentMimeType): string {
  switch (mime) {
    case 'application/pdf':
      return 'PDF';
    case 'image/gif':
      return 'GIF';
    case 'image/jpeg':
      return 'JPEG';
    case 'image/png':
      return 'PNG';
    case 'image/tiff':
      return 'TIFF';
    case 'image/bmp':
      return 'BMP';
    default: {
      const unexpected: never = mime;
      return unexpected;
    }
  }
}

type AllowedAttachmentMimeType = (typeof ALLOWED_ATTACHMENT_MIME_TYPES)[number];

interface AttachmentDraft {
  contentType: string;
  title: string;
  identifier: string;
  data?: string;
  url?: string;
  creation?: string;
}

function decodeRouteId(value: string | undefined): string | undefined {
  if (!value) return undefined;
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }
  return btoa(binary);
}

export function ComposeCareCommunication({
  mode,
  initialPatient,
  onClose,
}: {
  mode: SendMessageType;
  initialPatient?: {
    given: string;
    family: string;
    cpr: string;
    identifiers?: ReplyPatientIdentifiers;
  };
  onClose?: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { communicationIdentifier: rawId } = useParams<{ communicationIdentifier?: string }>();
  const communicationIdentifier = decodeRouteId(rawId);
  const [searchParams] = useSearchParams();
  const messageId = searchParams.get('messageId');
  const labReportId = searchParams.get('labReportId');

  const { activeClinicId, activeClinic } = useActiveClinic();
  const { data: profile } = useCurrentUserProfile();
  const { data: clinicUsers } = useClinicUsersList(activeClinicId, { includeInactive: false });
  const { data: clinic, isFetched: clinicFetched } = useClinic(activeClinicId);
  const send = useSendCareCommunication();
  const { data: connection } = useClinicCommunicateConnection(activeClinicId);
  const { data: extraSenderRows } = useClinicSenderIdentities(activeClinicId);
  const { data: threadData } = useCareCommunicationThread(
    mode === 'new' ? null : activeClinicId,
    mode === 'new' ? null : communicationIdentifier,
  );
  const bundleMessageId =
    mode === 'new'
      ? null
      : messageId ?? newestMessageId(threadData?.messages ?? [], true);
  const { data: letter, isError: letterFailed } = useBundleJson(
    activeClinicId,
    bundleMessageId,
  );
  const forwardChoice = useForwardChoice(letter, bundleMessageId ?? null, mode === 'forward');
  const loadThreadLetters = mode === 'reply' || mayCarryThreadAttachments(mode);
  const threadMessageIds = useMemo(
    () => (loadThreadLetters ? (threadData?.messages ?? []).map((item) => item.id) : []),
    [loadThreadLetters, threadData?.messages],
  );
  const { data: threadLetters } = useThreadLetterBundles(
    loadThreadLetters ? activeClinicId : null,
    threadMessageIds,
  );
  const replyParentId = useMemo(
    () =>
      mode === 'reply'
        ? replyParentMessageId({
            openedId: bundleMessageId,
            messages: threadData?.messages ?? [],
            letters: threadLetters ?? new Map(),
          })
        : null,
    [mode, bundleMessageId, threadData?.messages, threadLetters],
  );
  const threadAttachments = useMemo(
    () =>
      mayCarryThreadAttachments(mode)
        ? threadAttachmentsFromLetters(threadData?.messages ?? [], threadLetters ?? new Map())
        : [],
    [mode, threadData?.messages, threadLetters],
  );
  const forwardedIds = useMemo(
    () =>
      alreadyForwardedIdentifiers({
        mode,
        quotedAttachments: letter?.attachments ?? [],
        forwardSegmentIds: mode === 'forward' ? forwardChoice.segmentIds : undefined,
      }),
    [mode, letter?.attachments, forwardChoice.segmentIds],
  );
  const pickerAttachments = useMemo(
    () =>
      mayCarryThreadAttachments(mode)
        ? pickerThreadAttachments(threadAttachments, forwardedIds)
        : [],
    [mode, threadAttachments, forwardedIds],
  );

  const [category, setCategory] = useState<CareCommunicationCategory>('other');
  const [topic, setTopic] = useState('');
  const [priority, setPriority] = useState<'routine' | 'asap' | ''>('');
  const [messageText, setMessageText] = useState('');
  const [attachments, setAttachments] = useState<AttachmentDraft[]>([]);
  const [selectedThreadAttachments, setSelectedThreadAttachments] = useState<Set<string>>(
    () => new Set(),
  );
  const [attUrl, setAttUrl] = useState('');
  const [attUrlType, setAttUrlType] = useState<AllowedAttachmentMimeType>('application/pdf');
  const [attUrlTitle, setAttUrlTitle] = useState('');

  const [senderSorId, setSenderSorId] = useState('');
  const [senderName, setSenderName] = useState('');
  const [senderGln, setSenderGln] = useState('');
  const [receiverSorId, setReceiverSorId] = useState('');
  const [receiverName, setReceiverName] = useState('');
  const [receiverGln, setReceiverGln] = useState('');
  const [specificRecipientLabel, setSpecificRecipientLabel] = useState('');
  const [specificRecipient, setSpecificRecipient] = useState<ReplyRecipient | null>(null);
  const [specificRecipientCareTeam, setSpecificRecipientCareTeam] = useState<ReplyCareTeam | null>(
    null,
  );

  const [patientIdKind, setPatientIdKind] = useState<PatientIdKind>('cpr');
  const [patientIdValue, setPatientIdValue] = useState('');
  const [patientIdentifiers, setPatientIdentifiers] = useState<ReplyPatientIdentifiers>({});
  const [patientIdentityLocked, setPatientIdentityLocked] = useState(false);
  const [patientFamily, setPatientFamily] = useState('');
  const [patientGiven, setPatientGiven] = useState('');

  const [authorFamily, setAuthorFamily] = useState('');
  const [authorGiven, setAuthorGiven] = useState('');
  const [authorRoleCode, setAuthorRoleCode] = useState<string>('psykolog');
  const [authorPhone, setAuthorPhone] = useState('');
  const [authorUserId, setAuthorUserId] = useState('');
  const appliedAuthorUserId = useRef('');

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [prefilled, setPrefilled] = useState(false);
  const senderPicked = useRef(false);
  const clinicSender = useMemo(
    () =>
      composeClinicSender({
        sorId: connection?.sor_id,
        name: activeClinic?.name,
        ean: connection?.ean,
      }),
    [connection?.sor_id, connection?.ean, activeClinic?.name],
  );
  const extraSenders = useMemo(
    () =>
      (extraSenderRows ?? []).map((row) => ({
        sorId: row.sor,
        name: row.name,
        ean: row.ean,
      })),
    [extraSenderRows],
  );
  const authorizedSenders = useMemo(
    () =>
      clinicAuthorizedSenders(
        {
          sorId: connection?.sor_id,
          name: activeClinic?.name,
          ean: connection?.ean,
        },
        extraSenders,
      ),
    [connection?.sor_id, connection?.ean, activeClinic?.name, extraSenders],
  );
  const openedMessage = bundleMessageId
    ? threadData?.messages.find((item) => item.id === bundleMessageId)
    : threadData?.messages[threadData.messages.length - 1];
  const letterDirection =
    openedMessage?.direction === 'out'
      ? 'out'
      : openedMessage?.direction === 'in'
        ? 'in'
        : null;
  const senderChoices = useMemo(
    () =>
      replySenderChoices({
        mode,
        direction: letterDirection,
        clinic: clinicSender,
        letterReceiver: {
          sorId: letter?.receiverSor ?? openedMessage?.receiverSor,
          name: letter?.receiverOrganization ?? openedMessage?.receiverOrganization,
          ean: letter?.receiverGln,
        },
        authorized: authorizedSenders,
      }),
    [
      mode,
      letterDirection,
      clinicSender,
      letter?.receiverSor,
      letter?.receiverOrganization,
      letter?.receiverGln,
      openedMessage?.receiverSor,
      openedMessage?.receiverOrganization,
      authorizedSenders,
    ],
  );
  const preferredSender = useMemo(
    () =>
      preferredReplySender({
        mode,
        direction: letterDirection,
        clinic: clinicSender,
        letterReceiver: {
          sorId: letter?.receiverSor ?? openedMessage?.receiverSor,
          name: letter?.receiverOrganization ?? openedMessage?.receiverOrganization,
          ean: letter?.receiverGln,
        },
        authorized: authorizedSenders,
      }),
    [
      mode,
      letterDirection,
      clinicSender,
      letter?.receiverSor,
      letter?.receiverOrganization,
      letter?.receiverGln,
      openedMessage?.receiverSor,
      openedMessage?.receiverOrganization,
      authorizedSenders,
    ],
  );
  const canChooseSender = senderChoices.length > 1;
  const authorLocked = authorUserId !== AUTHOR_FREE_TEXT;
  const authorOptions = useMemo(() => {
    const users = (clinicUsers ?? [])
      .map((member) => member.user)
      .filter((user): user is NonNullable<typeof user> => Boolean(user));
    if (profile && !users.some((user) => user.id === profile.id)) {
      return [profile, ...users];
    }
    return users;
  }, [clinicUsers, profile]);

  useEffect(() => {
    if (senderChoices.length === 0) return;
    const selected = senderPicked.current
      ? senderChoices.find((choice) => choice.sorId === senderSorId) ?? preferredSender
      : preferredSender;
    if (!selected?.sorId) return;
    setSenderSorId(selected.sorId);
    setSenderName(selected.name);
    setSenderGln(selected.gln);
  }, [senderChoices, preferredSender, senderSorId]);

  const receiverNameFromSor = useSorEntityName(
    mode === 'reply' && !receiverName.trim() ? receiverSorId.trim() || null : null,
  );

  useEffect(() => {
    if (!profile?.id) return;
    setAuthorUserId((current) => current || profile.id);
  }, [profile?.id]);

  useEffect(() => {
    if (!authorUserId || authorUserId === AUTHOR_FREE_TEXT) return;
    const source =
      authorUserId === profile?.id
        ? profile
        : clinicUsers?.find((member) => member.user?.id === authorUserId)?.user;
    if (!source) return;
    if (!source.phone && !clinicFetched) return;
    const author = authorFromClinicUser(source, clinic);
    if (appliedAuthorUserId.current !== authorUserId) {
      setAuthorFamily(author.family);
      setAuthorGiven(author.given);
      setAuthorPhone(author.phone);
      appliedAuthorUserId.current = authorUserId;
      return;
    }
    setAuthorPhone((phone) => phone || author.phone);
  }, [authorUserId, profile, clinicUsers, clinic, clinicFetched]);

  useEffect(() => {
    if (mode !== 'reply' || receiverName.trim() || !receiverNameFromSor.name) return;
    setReceiverName(receiverNameFromSor.name);
  }, [mode, receiverName, receiverNameFromSor.name]);

  useForwardReceiverName({
    enabled: mode === 'forward',
    sorId: receiverSorId,
    name: receiverName,
    setName: setReceiverName,
    messages: threadData?.messages,
    letters: threadLetters,
    opened: letter,
  });

  usePatientGivenFallback({
    enabled: mode !== 'new' && prefilled,
    clinicId: activeClinicId,
    messageId: bundleMessageId,
    family: patientFamily,
    given: patientGiven,
    setGiven: setPatientGiven,
    messages: threadData?.messages,
    letters: threadLetters,
  });

  useEffect(() => {
    if (mode !== 'new' || !initialPatient || prefilled) return;
    setPatientGiven(initialPatient.given);
    setPatientFamily(initialPatient.family);
    const official = officialCprDigits(initialPatient.cpr);
    const identifiers = initialPatient.identifiers ?? (official ? { cpr: official } : {});
    const compose = newLetterPatientComposeState(identifiers);
    setPatientIdKind(compose.idKind);
    setPatientIdValue(compose.idValue);
    setPatientIdentifiers(compose.identifiers);
    setPatientIdentityLocked(compose.locked);
    setPrefilled(true);
  }, [mode, initialPatient, prefilled]);

  useEffect(() => {
    if (mode === 'new' || !threadData || prefilled) return;
    const opened = bundleMessageId
      ? threadData.messages.find((item) => item.id === bundleMessageId)
      : threadData.messages[threadData.messages.length - 1];
    const direction = opened?.direction === 'out' ? 'out' : 'in';
    if (bundleMessageId && !letter && !letterFailed) {
      if (opened?.category && isComposeCategory(opened.category)) {
        setCategory(opened.category);
      }
      if (opened?.topic) setTopic(opened.topic);
      if (mode === 'reply' && opened) {
        const fromUs = Boolean(senderSorId && opened.senderSor === senderSorId);
        if (direction === 'out' || fromUs) {
          setReceiverSorId(opened.receiverSor ?? '');
          if (opened.receiverOrganization) setReceiverName(opened.receiverOrganization);
        } else {
          setReceiverSorId(opened.senderSor ?? '');
          if (opened.senderOrganization) setReceiverName(opened.senderOrganization);
        }
      }
      return;
    }
    const assembled = threadData.assembled;
    const fromLetter = letter ? replyLetterFields(letter, direction, senderSorId) : null;
    const categoryCode = fromLetter?.categoryCode ?? assembled?.categoryCode ?? null;
    if (categoryCode && isComposeCategory(categoryCode)) setCategory(categoryCode);
    const topicText = fromLetter ? fromLetter.topic : assembled?.topic;
    if (topicText) setTopic(topicText);
    const patient = replyPatientFields(
      replyPatientSource({
        letter: letter
          ? {
              cpr: letter.cpr,
              xEcpr: letter.xEcpr,
              dEcpr: letter.dEcpr,
              family: letter.patientFamily,
              given: letter.patientGiven,
              patientName: letter.patientName,
            }
          : null,
        assembled: assembled?.patient,
      }),
    );
    if (patient) {
      if (patient.family) setPatientFamily(patient.family);
      if (patient.given) setPatientGiven(patient.given);
      if (patient.idValue) {
        setPatientIdKind(patient.idKind);
        setPatientIdValue(patient.idValue);
        setPatientIdentifiers(patient.identifiers);
        setPatientIdentityLocked(true);
      }
    }
    setPrefilled(true);
  }, [mode, threadData, prefilled, bundleMessageId, letter, letterFailed]);

  useEffect(() => {
    if (mode !== 'reply' || !letter || !letterDirection) return;
    const fromLetter = replyLetterFields(letter, letterDirection, senderSorId);
    const receiver = composeReceiver('reply', letter, letterDirection, senderSorId);
    if (!receiver) return;
    if (receiver.sorId) setReceiverSorId(receiver.sorId);
    if (receiver.name) setReceiverName(receiver.name);
    if (receiver.ean) setReceiverGln(receiver.ean);
    setSpecificRecipientLabel(fromLetter.specificRecipientLabel);
    setSpecificRecipient(fromLetter.recipient);
    setSpecificRecipientCareTeam(fromLetter.recipientCareTeam);
  }, [mode, letter, letterDirection, senderSorId]);

  const titleKey =
    mode === 'reply'
      ? 'communicate.compose_reply_title'
      : mode === 'forward'
        ? 'communicate.compose_forward_title'
        : 'communicate.compose_new_title';
  const identityBadge = patientIdentityLocked
    ? patientIdentityBadgeLabel(patientIdentifiers)
    : null;

  const onAttach = async (files: FileList | null) => {
    if (!files?.length) return;
    try {
      const next: AttachmentDraft[] = [];
      for (const file of Array.from(files)) {
        if (
          !(ALLOWED_ATTACHMENT_MIME_TYPES as readonly string[]).includes(file.type)
        ) {
          toast.error(t('communicate.errors.attachment_type_not_allowed'));
          continue;
        }
        const bytes = new Uint8Array(await file.arrayBuffer());
        const createdAt = new Date();
        next.push({
          contentType: file.type,
          title: attachmentTitle(file.name),
          identifier: newAttachmentIdentifier(),
          data: bytesToBase64(bytes),
          creation: attachmentCreationTime({
            lastModified: file.lastModified,
            createdAt,
          }),
        });
      }
      if (next.length) {
        setAttachments((prev) => [...prev, ...next]);
      }
    } catch {
      toast.error(t('communicate.errors.attachment_read'));
    }
  };

  const onAddUrlAttachment = () => {
    const url = attUrl.trim();
    if (!url) {
      toast.error(t('communicate.errors.attachment_url_required'));
      return;
    }
    setAttachments((prev) => [
      ...prev,
      {
        contentType: attUrlType,
        title: attUrlTitle.trim() || url,
        identifier: newAttachmentIdentifier(),
        url,
      },
    ]);
    setAttUrl('');
    setAttUrlType('application/pdf');
    setAttUrlTitle('');
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!activeClinicId) {
      setSubmitError(t('communicate.errors.no_clinic'));
      return;
    }
    if (
      !(clinicSender.sorId || senderSorId.trim()) ||
      !(clinicSender.name || senderName.trim())
    ) {
      setSubmitError(t('communicate.errors.sender_required'));
      return;
    }
    if (!receiverSorId.trim() || !receiverName.trim()) {
      setSubmitError(t('communicate.errors.receiver_required'));
      return;
    }
    const identifiers = patientIdentityLocked
      ? patientIdentifiers
      : patientIdentifiersFromField(patientIdKind, patientIdValue);
    if (!patientFamily.trim() || !hasPatientIdentifier(identifiers)) {
      setSubmitError(t('communicate.errors.patient_required'));
      return;
    }
    if (!authorFamily.trim() || !authorGiven.trim() || !authorPhone.trim()) {
      setSubmitError(t('communicate.errors.author_required'));
      return;
    }
    if (!messageText.trim() && attachments.length === 0) {
      setSubmitError(t('communicate.errors.message_required'));
      return;
    }
    if (topicRequired(category) && !topic.trim()) {
      setSubmitError(t('communicate.errors.topic_required'));
      return;
    }
    if (forwardChoice.nothingChosen) {
      setSubmitError(t('communicate.forward_choose_none'));
      return;
    }
    if (mode !== 'new' && !communicationIdentifier) {
      setSubmitError(t('communicate.errors.parent_required'));
      return;
    }

    if (identifiers.cpr && identifiers.cpr.length !== 10) {
      setSubmitError(t('communicate.errors.cpr_format'));
      return;
    }

    // Preserve linebreaks — content is sent as-is to Communicate.
    const payloads: PayloadSegmentInput[] = composeOutgoingPayloads({
      text: messageText,
      carried: mayCarryThreadAttachments(mode)
        ? carriedAttachmentPayloads(threadAttachments, selectedThreadAttachments, forwardedIds)
        : [],
      newAttachments: attachments.map((att) => ({
        identifier: att.identifier,
        contentType: att.contentType,
        title: att.title,
        data: att.data,
        url: att.url,
        creation: att.creation,
      })),
    });

    const patient = {
      family: patientFamily.trim(),
      given: patientGiven
        .trim()
        .split(/\s+/)
        .filter(Boolean),
      ...identifiers,
    };

    // A forward sends the letter on screen. A reply uses the inbound row whose
    // stored bundle still has the full received history (Communicate copies
    // priors only from parent.bundle_json).
    const parentMessageId =
      mode === 'forward'
        ? bundleMessageId
        : mode === 'reply'
          ? (replyParentId ?? bundleMessageId)
          : undefined;

    setSubmitting(true);
    try {
      const citedLabIds = labReportId ? [labReportId] : [];
      if (mode === 'forward' || citedLabIds.length > 0) {
        const allowed = await labShareCheck(activeClinicId, {
          messageId: parentMessageId ?? undefined,
          labReportIds: citedLabIds,
        });
        if (!allowed) {
          setSubmitError(t('patients.lab.forward_blocked'));
          return;
        }
      }
      const result = await send.mutateAsync({
        clinicId: activeClinicId,
        type: mode,
        ...(citedLabIds.length > 0 ? { labReportIds: citedLabIds } : {}),
        ...(mode !== 'new'
          ? {
              parent: {
                ...(parentMessageId ? { messageId: parentMessageId } : {}),
                communicationIdentifier,
              },
            }
          : {}),
        sender: {
          sorId: senderSorId.trim() || clinicSender.sorId,
          name: senderName.trim() || clinicSender.name,
          ...(senderGln.trim() || clinicSender.gln
            ? { gln: senderGln.trim() || clinicSender.gln }
            : {}),
        },
        receiver: {
          sorId: receiverSorId.trim(),
          name: receiverName.trim(),
          ...(receiverGln.trim() ? { gln: receiverGln.trim() } : {}),
        },
        patient,
        category,
        topic: outgoingTopic(topic),
        ...(category === 'regarding-referral' && priority
          ? { priority }
          : {}),
        author: {
          family: authorFamily.trim(),
          given: authorGiven
            .trim()
            .split(/\s+/)
            .filter(Boolean),
          roleCode: authorRoleCode,
          phone: authorPhone.trim(),
        },
        ...(mode === 'reply'
          ? specificRecipientBody({
              label: specificRecipientLabel,
              recipient: specificRecipient,
              careTeam: specificRecipientCareTeam,
            })
          : {}),
        ...(mode !== 'new' ? replyEncounterPayload(letter) ?? {} : {}),
        ...(mode === 'forward' && forwardChoice.segmentIds
          ? { forwardSegmentIds: forwardChoice.segmentIds }
          : {}),
        payloads,
      });

      toast.success(t('communicate.toast_sent'));
      if (onClose) {
        onClose();
      } else {
        navigate(
          `/medcom/letters/${encodeURIComponent(result.communicationIdentifier)}`,
        );
      }
    } catch (err) {
      if (err instanceof ApiCallError) {
        if (err.code === 'invalid-input') {
          setSubmitError(t('communicate.errors.invalid_input'));
        } else if (err.code === 'forbidden') {
          setSubmitError(
            /nægtet samtykke/i.test(err.message)
              ? t('patients.lab.forward_blocked')
              : t('communicate.errors.forbidden'),
          );
        } else {
          setSubmitError(err.message);
        }
      } else {
        setSubmitError(t('communicate.errors.unknown'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const backTo =
    mode !== 'new' && communicationIdentifier
      ? `/medcom/letters/${encodeURIComponent(communicationIdentifier)}`
      : '/medcom/letters';

  const leave = () => {
    if (onClose) onClose();
    else navigate(backTo);
  };

  return (
    <div>
      <div>
        <div>
          <button
            type="button"
            onClick={leave}
            className="mb-4 inline-flex items-center text-sm text-ds-muted hover:text-ds-text"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('communicate.back')}
          </button>

          <h2 className={PAGE_TITLE}>
            {t(titleKey)}
          </h2>
          <p className="mt-1 text-sm text-ds-muted">
            {t('communicate.compose_subtitle')}
          </p>

          {submitError && (
            <div className="mt-4">
              <ErrorAlert message={submitError} />
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-8">
            <section className="space-y-4">
              <h3 className="text-sm font-semibold text-ds-text">
                {t('communicate.section_message')}
              </h3>
              <div>
                <label className="block text-sm font-medium text-ds-text">
                  {t('communicate.field_category')}
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as CareCommunicationCategory)}
                  className="mt-1 block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-sm shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal"
                >
                  {[...CARE_COMMUNICATION_CATEGORIES]
                    .sort((category, otherCategory) => {
                      const label = t(`communicate.category_${category}`);
                      const other = t(`communicate.category_${otherCategory}`);
                      return label.localeCompare(other, 'da');
                    })
                    .map((c) => (
                      <option key={c} value={c}>
                        {t(`communicate.category_${c}`)}
                      </option>
                    ))}
                </select>
              </div>
              <Input
                label={t('communicate.field_topic')}
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                required={topicRequired(category)}
              />
              {category === 'regarding-referral' && (
                <div>
                  <label className="block text-sm font-medium text-ds-text">
                    {t('communicate.field_priority')}
                  </label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value as 'routine' | 'asap' | '')}
                    className="mt-1 block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-sm shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal"
                  >
                    <option value="">{t('communicate.priority_none')}</option>
                    <option value="routine">{t('communicate.priority_routine')}</option>
                    <option value="asap">{t('communicate.priority_asap')}</option>
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-ds-text">
                  {t('communicate.field_message')}
                </label>
                {(mode === 'reply' || mode === 'forward') && (
                  <div className="mt-2">
                    <ForwardPriorMessage
                      letter={letter}
                      threadSegments={threadData?.assembled?.segments}
                      choice={mode === 'forward' ? forwardChoice : undefined}
                    />
                  </div>
                )}
                <textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  rows={8}
                  className="mt-1 block w-full whitespace-pre-wrap rounded-md border border-ds-field bg-ds-card px-3 py-2 text-sm shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal"
                  placeholder={t('communicate.field_message_placeholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ds-text">
                  {t('communicate.field_attachments')}
                </label>
                {mayCarryThreadAttachments(mode) && (
                  <ThreadAttachmentPicker
                    attachments={pickerAttachments}
                    selected={selectedThreadAttachments}
                    onToggle={(key) => {
                      setSelectedThreadAttachments((prev) => {
                        const next = new Set(prev);
                        if (next.has(key)) next.delete(key);
                        else next.add(key);
                        return next;
                      });
                    }}
                  />
                )}
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <label className="inline-flex cursor-pointer items-center rounded-md border border-ds-field bg-ds-card px-3 py-1.5 text-sm text-ds-text shadow-card hover:bg-ds-row-hover">
                    <Paperclip className="mr-1 h-4 w-4" />
                    {t('communicate.add_attachment')}
                    <input
                      type="file"
                      className="hidden"
                      multiple
                      accept="application/pdf,image/gif,image/jpeg,image/png,image/tiff,image/bmp"
                      onChange={(e) => {
                        void onAttach(e.target.files);
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>
                {attachments.length > 0 && (
                  <ul className="mt-3 space-y-2">
                    {attachments.map((att, idx) => (
                      <li
                        key={att.identifier}
                        className="rounded-md border border-ds-line bg-ds-sunk px-3 py-2 text-sm"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-medium text-ds-text">{att.title}</p>
                          <button
                            type="button"
                            className="text-ds-muted hover:text-ds-text"
                            onClick={() =>
                              setAttachments((prev) => prev.filter((_, i) => i !== idx))
                            }
                            aria-label={t('communicate.remove_attachment')}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        <p className="mt-1 font-mono text-xs text-ds-muted">
                          {t('communicate.field_attachment_identifier')}: {att.identifier}
                        </p>
                        {att.creation ? (
                          <p className="mt-0.5 text-xs text-ds-muted">
                            {t('communicate.field_attachment_creation')}: {att.creation}
                          </p>
                        ) : null}
                        {(authorGiven.trim() || authorFamily.trim() || authorPhone.trim()) && (
                          <p className="mt-0.5 text-xs text-ds-muted">
                            {t('communicate.field_attachment_author')}:{' '}
                            {[authorGiven.trim(), authorFamily.trim()].filter(Boolean).join(' ')}
                            {authorPhone.trim() ? ` · ${authorPhone.trim()}` : ''}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto_1fr_auto] sm:items-end">
                  <Input
                    label={t('communicate.field_attachment_url')}
                    value={attUrl}
                    onChange={(e) => setAttUrl(e.target.value)}
                  />
                  <div>
                    <label className="block text-sm font-medium text-ds-text">
                      {t('communicate.field_attachment_type')}
                    </label>
                    <select
                      value={attUrlType}
                      onChange={(e) =>
                        setAttUrlType(e.target.value as AllowedAttachmentMimeType)
                      }
                      className="mt-1 block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-sm shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal"
                    >
                      {ALLOWED_ATTACHMENT_MIME_TYPES.map((mime) => (
                        <option key={mime} value={mime}>
                          {mimeLabel(mime)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Input
                    label={t('communicate.field_attachment_title')}
                    value={attUrlTitle}
                    onChange={(e) => setAttUrlTitle(e.target.value)}
                  />
                  <Button type="button" variant="secondary" onClick={onAddUrlAttachment}>
                    {t('communicate.add_attachment_url')}
                  </Button>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h3 className="text-sm font-semibold text-ds-text">
                {t('communicate.section_parties')}
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                {canChooseSender && (
                  <div className="sm:col-span-2">
                    <label
                      className="block text-sm font-medium text-ds-text"
                      htmlFor="sender-choice"
                    >
                      {t('communicate.field_sender_choice')}
                    </label>
                    <select
                      id="sender-choice"
                      value={senderSorId}
                      onChange={(e) => {
                        senderPicked.current = true;
                        setSenderSorId(e.target.value);
                      }}
                      className="mt-1 block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-sm shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal"
                    >
                      {senderChoices.map((choice) => (
                        <option key={choice.sorId} value={choice.sorId}>
                          {[
                            choice.name,
                            choice.sorId ? `SOR ${choice.sorId}` : '',
                            choice.gln ? `EAN ${choice.gln}` : '',
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <Input
                  label={t('communicate.field_sender_sor')}
                  value={senderSorId}
                  onChange={(e) => setSenderSorId(e.target.value)}
                  readOnly={mode !== 'new' || canChooseSender}
                  className={mode !== 'new' || canChooseSender ? 'bg-ds-sunk ' : undefined}
                  required
                />
                <Input
                  label={t('communicate.field_sender_name')}
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  readOnly={mode !== 'new' || canChooseSender}
                  className={mode !== 'new' || canChooseSender ? 'bg-ds-sunk ' : undefined}
                  required
                />
                {mode !== 'new' && (
                  <Input
                    label={t('communicate.field_sender_ean')}
                    value={senderGln}
                    readOnly
                    className="bg-ds-sunk font-mono"
                  />
                )}
                <Input
                  label={t('communicate.field_receiver_sor')}
                  value={receiverSorId}
                  onChange={(e) => setReceiverSorId(e.target.value)}
                  required
                />
                <Input
                  label={t('communicate.field_receiver_name')}
                  value={receiverName}
                  onChange={(e) => setReceiverName(e.target.value)}
                  required
                />
                {mode === 'reply' && receiverGln.trim().length > 0 && (
                  <Input
                    label={t('communicate.field_receiver_ean')}
                    value={receiverGln}
                    readOnly
                    className="bg-ds-sunk font-mono"
                  />
                )}
                {mode === 'reply' && (
                  <div className="sm:col-span-2">
                    <label
                      className="block text-sm font-medium text-ds-text"
                      htmlFor="specific-recipient"
                    >
                      {t('communicate.field_specific_recipient')}
                    </label>
                    <div className="mt-1 flex items-center gap-2">
                      <Input
                        id="specific-recipient"
                        value={specificRecipientLabel}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSpecificRecipientLabel(value);
                          if (!value.trim()) {
                            setSpecificRecipient(null);
                            setSpecificRecipientCareTeam(null);
                          }
                        }}
                      />
                      {specificRecipientLabel.trim().length > 0 && (
                        <button
                          type="button"
                          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-ds-muted hover:bg-ds-row-hover hover:text-ds-text"
                          aria-label={t('communicate.remove_specific_recipient')}
                          onClick={() => {
                            setSpecificRecipientLabel('');
                            setSpecificRecipient(null);
                            setSpecificRecipientCareTeam(null);
                          }}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </section>

            {mode !== 'new' && (letter?.episodeOfCareIdentifiers.length ?? 0) > 0 && (
              <section className="space-y-4">
                <h3 className="text-sm font-semibold text-ds-text">
                  {t('communicate.section_episode')}
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  {letter?.episodeOfCareIdentifiers.map((episode) => (
                    <Input
                      key={`${episode.system ?? ''}:${episode.rawValue}`}
                      label={episodeOfCareLabel(episode.source)}
                      value={episode.value}
                      readOnly
                      className="bg-ds-sunk font-mono"
                    />
                  ))}
                </div>
              </section>
            )}

            <section className="space-y-4">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-ds-text">
                <span>{t('communicate.section_patient')}</span>
                {identityBadge ? (
                  <span className="inline-flex items-center rounded-full bg-ds-green-tint px-2 py-0.5 text-xs font-medium text-ds-green-ink">
                    <CheckCircle2 className="mr-1 h-3 w-3" />
                    {identityBadge}
                  </span>
                ) : null}
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                {patientIdentityLocked && listedPatientIdentifiers(patientIdentifiers).length > 0 ? (
                  listedPatientIdentifiers(patientIdentifiers).map((identifier) => (
                    <Input
                      key={identifier.kind}
                      label={t(`communicate.id_kind_${identifier.kind}`)}
                      value={identifier.value}
                      readOnly
                      className="bg-ds-sunk font-mono"
                    />
                  ))
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-ds-text">
                        {t('communicate.field_patient_id_kind')}
                      </label>
                      <select
                        value={patientIdKind}
                        onChange={(e) => setPatientIdKind(e.target.value as PatientIdKind)}
                        className="mt-1 block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-sm shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal"
                      >
                        <option value="cpr">{t('communicate.id_kind_cpr')}</option>
                        <option value="xEcpr">{t('communicate.id_kind_xEcpr')}</option>
                        <option value="dEcpr">{t('communicate.id_kind_dEcpr')}</option>
                      </select>
                    </div>
                    <Input
                      label={t('communicate.field_patient_id')}
                      value={patientIdValue}
                      onChange={(e) => setPatientIdValue(e.target.value)}
                      autoComplete="off"
                      required
                    />
                  </>
                )}
                <Input
                  label={t('communicate.field_patient_family')}
                  value={patientFamily}
                  onChange={(e) => setPatientFamily(e.target.value)}
                  required
                />
                <Input
                  label={t('communicate.field_patient_given')}
                  value={patientGiven}
                  onChange={(e) => setPatientGiven(e.target.value)}
                />
              </div>
            </section>

            <section className="space-y-4">
              <h3 className="text-sm font-semibold text-ds-text">
                {t('communicate.section_author')}
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label
                    className="block text-sm font-medium text-ds-text"
                    htmlFor="author-choice"
                  >
                    {t('communicate.field_author_choice')}
                  </label>
                  <select
                    id="author-choice"
                    value={authorUserId}
                    onChange={(e) => {
                      const next = e.target.value;
                      setAuthorUserId(next);
                      if (next === AUTHOR_FREE_TEXT) {
                        appliedAuthorUserId.current = AUTHOR_FREE_TEXT;
                        return;
                      }
                      appliedAuthorUserId.current = '';
                    }}
                    className="mt-1 block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-sm shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal"
                  >
                    {authorOptions.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name?.trim() || user.email}
                      </option>
                    ))}
                    <option value={AUTHOR_FREE_TEXT}>
                      {t('communicate.author_free_text')}
                    </option>
                  </select>
                </div>
                <Input
                  label={t('communicate.field_author_family')}
                  value={authorFamily}
                  onChange={(e) => setAuthorFamily(e.target.value)}
                  readOnly={authorLocked}
                  className={authorLocked ? 'bg-ds-sunk ' : undefined}
                  required
                />
                <Input
                  label={t('communicate.field_author_given')}
                  value={authorGiven}
                  onChange={(e) => setAuthorGiven(e.target.value)}
                  readOnly={authorLocked}
                  className={authorLocked ? 'bg-ds-sunk ' : undefined}
                  required
                />
                <div>
                  <label className="block text-sm font-medium text-ds-text">
                    {t('communicate.field_author_role')}
                  </label>
                  <select
                    value={authorRoleCode}
                    onChange={(e) => setAuthorRoleCode(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-sm shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal"
                  >
                    {AUTHOR_ROLE_CODES.map((code) => (
                      <option key={code} value={code}>
                        {t(`communicate.role_${code}`, { defaultValue: code })}
                      </option>
                    ))}
                  </select>
                </div>
                <Input
                  label={t('communicate.field_author_phone')}
                  value={authorPhone}
                  onChange={(e) => setAuthorPhone(e.target.value)}
                  required
                />
              </div>
            </section>

            <div className="flex justify-end gap-3">
              <Button type="button" variant="secondary" onClick={leave}>
                {t('communicate.cancel')}
              </Button>
              <Button type="submit" disabled={submitting || !activeClinicId}>
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('communicate.send')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function isComposeCategory(code: string): code is CareCommunicationCategory {
  return (CARE_COMMUNICATION_CATEGORIES as readonly string[]).includes(code);
}

function hasPatientIdentifier(identifiers: ReplyPatientIdentifiers): boolean {
  return Boolean(identifiers.cpr || identifiers.xEcpr || identifiers.dEcpr);
}

export function NewCareCommunication() {
  return <ComposeCareCommunication mode="new" />;
}

export function ReplyCareCommunication() {
  return <ComposeCareCommunication mode="reply" />;
}

export function ForwardCareCommunication() {
  return <ComposeCareCommunication mode="forward" />;
}
